import React, { useState, useEffect } from "react";

const CSS = `
  .pf-preloader {
    position: fixed; inset: 0; z-index: 99999; background: #0A0E1A;
    display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 24px;
    transition: opacity 0.7s ease, visibility 0.7s ease;
  }
  .pf-preloader.hidden { opacity: 0; visibility: hidden; pointer-events: none; }

  /* Compass container */
  .pf-pre-compass {
    width: 100px; height: 100px; position: relative;
    display: flex; align-items: center; justify-content: center;
  }

  /* Outer orbit ring */
  .pf-pre-compass::before {
    content: ''; position: absolute; inset: -12px; border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: #FF6B35; border-right-color: #FFD23F;
    animation: pfCompassRing 1.8s linear infinite;
    filter: drop-shadow(0 0 8px rgba(255,107,53,0.4));
  }
  .pf-pre-compass::after {
    content: ''; position: absolute; inset: -4px; border-radius: 50%;
    border: 1.5px solid transparent;
    border-bottom-color: #00D4AA; border-left-color: #7C5CFC;
    animation: pfCompassRing 2.5s linear infinite reverse;
    filter: drop-shadow(0 0 6px rgba(0,212,170,0.3));
  }
  @keyframes pfCompassRing { to { transform: rotate(360deg); } }

  /* Center icon */
  .pf-pre-icon {
    font-size: 2.8em; z-index: 1; position: relative;
    animation: pfCompassPulse 2s ease-in-out infinite;
    filter: drop-shadow(0 0 20px rgba(255,107,53,0.5));
  }
  @keyframes pfCompassPulse {
    0%,100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 16px rgba(255,107,53,0.4)); }
    25% { transform: scale(1.05) rotate(15deg); }
    50% { transform: scale(1.08) rotate(0deg); filter: drop-shadow(0 0 30px rgba(255,107,53,0.7)) drop-shadow(0 0 50px rgba(0,212,170,0.3)); }
    75% { transform: scale(1.05) rotate(-15deg); }
  }

  /* Orbiting particles */
  .pf-pre-orbit { position: absolute; inset: -20px; border-radius: 50%; }
  .pf-pre-orb1 { animation: pfCompassRing 7s linear infinite; }
  .pf-pre-orb1::before {
    content: '🎯'; position: absolute; top: -8px; left: 50%; transform: translateX(-50%);
    font-size: 0.9em; filter: drop-shadow(0 0 8px rgba(255,210,63,0.8));
  }
  .pf-pre-orb2 { animation: pfCompassRing 5s linear infinite reverse; }
  .pf-pre-orb2::before {
    content: '✨'; position: absolute; bottom: -8px; right: 10%;
    font-size: 0.8em; filter: drop-shadow(0 0 6px rgba(0,212,170,0.6));
  }
  .pf-pre-orb3 { animation: pfCompassRing 9s linear infinite; }
  .pf-pre-orb3::before {
    content: '💡'; position: absolute; top: 20%; left: -10px;
    font-size: 0.75em;
  }

  /* Brand name */
  .pf-pre-brand {
    font-family: 'DM Serif Display', serif; font-size: 1.4em; color: #F0F0F5;
    letter-spacing: -0.5px; animation: pfBrandFade 0.8s ease 0.3s both;
  }
  .pf-pre-brand em {
    font-style: italic;
    background: linear-gradient(135deg, #FF6B35, #FFD23F);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  @keyframes pfBrandFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  /* Loading dots */
  .pf-pre-dots { display: flex; gap: 8px; }
  .pf-pre-dot {
    width: 6px; height: 6px; border-radius: 50%;
    animation: pfDotBounce 1.4s ease-in-out infinite;
  }
  .pf-pre-dot:nth-child(1) { background: #FF6B35; animation-delay: 0s; }
  .pf-pre-dot:nth-child(2) { background: #FFD23F; animation-delay: 0.2s; }
  .pf-pre-dot:nth-child(3) { background: #00D4AA; animation-delay: 0.4s; }
  .pf-pre-dot:nth-child(4) { background: #7C5CFC; animation-delay: 0.6s; }
  @keyframes pfDotBounce { 0%,100%{transform:translateY(0);opacity:0.4} 50%{transform:translateY(-10px);opacity:1} }

  /* Status text */
  .pf-pre-status {
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.72em; font-weight: 600;
    letter-spacing: 2px; text-transform: uppercase;
    color: rgba(255,107,53,0.4); animation: pfStatusPulse 2s ease-in-out infinite;
  }
  @keyframes pfStatusPulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }

  /* Progress bar */
  .pf-pre-progress { width: 180px; height: 3px; border-radius: 3px; background: rgba(255,255,255,0.04); overflow: hidden; }
  .pf-pre-progress-fill {
    height: 100%; border-radius: 3px;
    background: linear-gradient(90deg, #FF6B35, #FFD23F, #00D4AA, #7C5CFC);
    background-size: 200% 100%; animation: pfProgressFill 2s ease-in-out infinite;
  }
  @keyframes pfProgressFill { 0%{width:0%;margin-left:0} 50%{width:100%;margin-left:0} 100%{width:0%;margin-left:100%} }
`;

const STATUS_MSGS = [
  "Mapping career galaxies",
  "Scanning talent dimensions",
  "Charting your future",
  "Discovering pathways",
];

export default function Preloader({ loading }) {
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    const iv = setInterval(
      () => setMsgIdx((i) => (i + 1) % STATUS_MSGS.length),
      600,
    );
    return () => clearInterval(iv);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className={`pf-preloader${loading ? "" : " hidden"}`}>
        <div className="pf-pre-compass">
          <div className="pf-pre-orbit pf-pre-orb1" />
          <div className="pf-pre-orbit pf-pre-orb2" />
          <div className="pf-pre-orbit pf-pre-orb3" />
          <span className="pf-pre-icon">🧭</span>
        </div>
        <div className="pf-pre-brand">
          Path<em>finder</em>
        </div>
        <div className="pf-pre-dots">
          <div className="pf-pre-dot" />
          <div className="pf-pre-dot" />
          <div className="pf-pre-dot" />
          <div className="pf-pre-dot" />
        </div>
        <div className="pf-pre-progress">
          <div className="pf-pre-progress-fill" />
        </div>
        <div className="pf-pre-status">{STATUS_MSGS[msgIdx]}</div>
      </div>
    </>
  );
}
